import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import type { VampCaid, VampDay, VampAccount } from "../../../types/vampRates";

dayjs.extend(utc);

// Visa Acquirer Monitoring Program: the excessive line, and the monthly event count Visa needs
// before it scores a CAID at all. No server thresholds exist; these are the UI's.
export const VAMP_EXCESSIVE_PCT = 1.5;
export const VAMP_SCORING_THRESHOLD = 1500;
export const VAMP_LISTED_CAIDS = 5;
export const VAMP_PACE_LIKELY = 1.05;
export const VAMP_PACE_MAYBE = 0.85;
export const VAMP_RATE_BAR_MAX_PCT = 4;

export interface VampTotals {
  settled: number;
  tc40: number;
  tc15: number;
  events: number;
}

export const vampRatePct = (events: number, settled: number): number => (settled > 0 ? (events / settled) * 100 : 0);

export const fmtInt = (n: number): string => Math.round(n).toLocaleString("en-US");
export const fmtPct = (pct: number): string => `${pct.toFixed(2)}%`;

export function sumTotals(rows: { settled: number; tc40: number; tc15: number }[]): VampTotals {
  const t = { settled: 0, tc40: 0, tc15: 0 };
  for (const row of rows) {
    t.settled += row.settled;
    t.tc40 += row.tc40;
    t.tc15 += row.tc15;
  }
  return { ...t, events: t.tc40 + t.tc15 };
}

// Only the requested month's days count towards its totals; the previous month rides along for the chart.
const monthDays = (days: VampDay[], month: string): VampDay[] => days.filter((d) => d.date.startsWith(month));

export const caidTotals = (caid: VampCaid, month: string): VampTotals => sumTotals(monthDays(caid.days, month));

// The server sends the account's own series, so it is read rather than summed from the rows.
export const accountTotals = (account: VampAccount, month: string): VampTotals =>
  sumTotals(monthDays(account.days, month));

export interface VampMonthContext {
  month: string;
  isCurrent: boolean;
  dayOfMonth: number;
  daysInMonth: number;
  elapsed: number;
}

export function monthContext(month: string, today: string): VampMonthContext {
  const start = dayjs.utc(`${month}-01`);
  const daysInMonth = start.daysInMonth();
  const isCurrent = dayjs.utc(today).format("YYYY-MM") === month;
  const dayOfMonth = isCurrent ? dayjs.utc(today).date() : daysInMonth;
  return { month, isCurrent, dayOfMonth, daysInMonth, elapsed: dayOfMonth / daysInMonth };
}

export type VampScopeState = "past" | "short" | "likely" | "maybe" | "unlikely";

export function scopeState(events: number, elapsed: number): VampScopeState {
  if (events >= VAMP_SCORING_THRESHOLD) return "past";
  if (elapsed >= 1) return "short";
  const pace = events / elapsed / VAMP_SCORING_THRESHOLD;
  if (pace >= VAMP_PACE_LIKELY) return "likely";
  if (pace >= VAMP_PACE_MAYBE) return "maybe";
  return "unlikely";
}

export const willScore = (state: VampScopeState): boolean => state === "past" || state === "likely" || state === "maybe";

export type VampStanding = "Excessive" | "Above Standard" | "Good Standing" | "Safe";

// Visa's own vocabulary. Excessive is a fact (both tests met); Above Standard is a forecast.
// An unmatched row is not a standing of its own: it reads "Safe" off its zero events.
function standingOf(state: VampScopeState, ratePct: number, rated: boolean): VampStanding {
  if (!rated) return "Safe";
  if (state === "past" && ratePct >= VAMP_EXCESSIVE_PCT) return "Excessive";
  if (!willScore(state)) return "Safe";
  return ratePct < VAMP_EXCESSIVE_PCT ? "Good Standing" : "Above Standard";
}

function rateInkClass(ratePct: number, scored: boolean, unmatched: boolean): string {
  if (unmatched) return "text-gray-300";
  if (!scored) return "text-gray-400";
  return ratePct >= VAMP_EXCESSIVE_PCT ? "text-error-700" : "text-gray-900";
}

// Everything a row renders from its month totals. Derived once so the list, the breakdown, the
// detail header, the history table and the scoring steps cannot drift on what "scored" means.
export interface VampReading {
  totals: VampTotals;
  unmatched: boolean;
  state: VampScopeState;
  // Whether the month has any settled volume to divide by. Without it there is no rate at all,
  // which is not the same as a rate of 0% and must never read as one.
  rated: boolean;
  scored: boolean;
  rate: number;
  standing: VampStanding;
  rateInk: string;
  rateLabel: string;
}

export function reading(totals: VampTotals, elapsed: number, unmatched: boolean): VampReading {
  const state = scopeState(totals.events, elapsed);
  const rated = totals.settled > 0;
  const scored = !unmatched && rated && willScore(state);
  const rate = vampRatePct(totals.events, totals.settled);
  return {
    totals,
    unmatched,
    state,
    rated,
    scored,
    rate,
    standing: standingOf(state, rate, rated),
    rateInk: rateInkClass(rate, scored, unmatched),
    rateLabel: unmatched || !rated ? "–" : fmtPct(rate),
  };
}

export const caidReading = (caid: VampCaid, ctx: VampMonthContext): VampReading =>
  reading(caidTotals(caid, ctx.month), ctx.elapsed, isUnmatched(caid));

export const accountReading = (account: VampAccount, ctx: VampMonthContext): VampReading =>
  reading(accountTotals(account, ctx.month), ctx.elapsed, accountUnmatched(account));

export const isUnmatched = (caid: VampCaid): boolean => caid.caid === null;

// Beyond this the list stops being readable, and the count already carries the size of the hole.
const MAX_LISTED_DAYS = 5;

const listDays = (days: string[]): string => {
  const shown = days.slice(0, MAX_LISTED_DAYS).map((day) => dayjs.utc(day).format("D MMM"));
  const rest = days.length - shown.length;
  return rest > 0 ? `${shown.join(", ")} and ${rest} more` : shown.join(", ");
};

// A day with no settled-volume row at all is not a zero-volume day: it never synced, so the rate
// it feeds is divided by less than the month actually did.
//
// Only days in the rated month can distort the rate on screen, but the rest are still reported:
// the nightly job fills the current month and the one before it, so anything older is a backlog
// someone has to backfill by hand, and this warning is where they see it.
export function missingDaysWarning({ missingDays }: VampCaid, month: string): string | null {
  if (missingDays.length === 0) return null;

  const rated = missingDays.filter((day) => day.startsWith(month));
  const earlier = missingDays.length - rated.length;
  const backlog = `${earlier} earlier ${earlier === 1 ? "day is" : "days are"} missing`;

  if (rated.length === 0) {
    return `Settled Visa volume is complete for this month, but ${backlog} (${listDays(missingDays)}).`;
  }
  const rate = `Settled Visa volume is missing for ${rated.length} ${rated.length === 1 ? "day" : "days"} in this month (${listDays(rated)}), so this rate is measured against an incomplete denominator.`;
  return earlier === 0 ? rate : `${rate} A further ${backlog}.`;
}

// Everything wrong with one row. Only the denominator is reported, and only to a viewer holding
// vamp:all - the server sends a client no missing days at all.
export const caidWarnings = (caid: VampCaid, month: string): string[] =>
  [missingDaysWarning(caid, month)].filter((message) => message !== null);

// An account has no problems of its own - every one it can report belongs to a row - and its rows
// all carry the same missing-days list, so the distinct union is exact. It is also what the
// account row's icon has to stand for once its rows are collapsed beneath it.
export const accountWarnings = (account: VampAccount, month: string): string[] =>
  [...new Set(account.caids.flatMap((caid) => caidWarnings(caid, month)))];

export const accountUnmatched = (account: VampAccount): boolean => account.caids.every(isUnmatched);

export const STANDING_TIPS: Record<VampStanding, string> = {
  Excessive: "This CAID has passed the 1,500 events threshold and its rate is above 1.5%.",
  "Above Standard":
    "On course to pass the 1,500 events threshold, and the rate currently is above 1.5%. This may be problematic if this trend continues.",
  "Good Standing": "Visa will score this CAID this month, but it is within the 1.5% allowed rate.",
  Safe:
    "Visa will not score this CAID this month: it is either under the 1,500 events threshold, or it has no settled volume to rate against.",
};

// Widths (0-100) of the recorded and projected segments of a bar that runs to 1,500 events.
export function scopeBar(events: number, elapsed: number): { usedPct: number; projectedPct: number } {
  return {
    usedPct: Math.min(100, (events / VAMP_SCORING_THRESHOLD) * 100),
    projectedPct: Math.min(100, (events / elapsed / VAMP_SCORING_THRESHOLD) * 100),
  };
}

const eventsDesc = (a: VampTotals, b: VampTotals): number => b.events - a.events;

export function sortAccounts(accounts: VampAccount[], month: string): VampAccount[] {
  return [...accounts].sort(
    (a, b) => Number(accountUnmatched(a)) - Number(accountUnmatched(b)) || eventsDesc(accountTotals(a, month), accountTotals(b, month)),
  );
}

export function sortCaids(account: VampAccount, month: string): VampCaid[] {
  return [...account.caids].sort(
    (a, b) => Number(isUnmatched(a)) - Number(isUnmatched(b)) || eventsDesc(caidTotals(a, month), caidTotals(b, month)),
  );
}

// The Overview table: CAIDs Visa will score first, then by events.
export function rankForBreakdown(account: VampAccount, ctx: VampMonthContext): VampCaid[] {
  const scoring = (caid: VampCaid) => !isUnmatched(caid) && willScore(scopeState(caidTotals(caid, ctx.month).events, ctx.elapsed));
  return [...account.caids].sort(
    (a, b) => Number(scoring(b)) - Number(scoring(a)) || eventsDesc(caidTotals(a, ctx.month), caidTotals(b, ctx.month)),
  );
}

export const accountKey = (account: VampAccount): string => `a:${account.id}`;
export const caidKey = (caid: VampCaid): string => `c:${caid.id}`;

export type VampSelection =
  | { kind: "account"; account: VampAccount }
  | { kind: "caid"; account: VampAccount; caid: VampCaid };

// A stale key (company switched, month changed) falls back to the first account so the list
// highlight and the detail panel always agree on a row.
export function resolveSelection(accounts: VampAccount[], key: string | null): VampSelection | null {
  for (const account of accounts) {
    if (accountKey(account) === key) return { kind: "account", account };
    const caid = account.caids.find((c) => caidKey(c) === key);
    if (caid) return { kind: "caid", account, caid };
  }
  return accounts[0] ? { kind: "account", account: accounts[0] } : null;
}

export function summaryLine(totals: VampTotals, state: VampScopeState, isCurrent: boolean): string {
  const when = isCurrent ? "so far this month" : "in this month";
  if (totals.settled === 0) {
    return `${fmtInt(totals.events)} events recorded ${when}, but no settled Visa transactions are recorded for it, so there is no rate to measure.`;
  }
  const volume = `${fmtInt(totals.settled)} Visa transactions`;
  const rate = fmtPct(vampRatePct(totals.events, totals.settled));
  if (state === "past") return `${fmtInt(totals.events)} events recorded ${when} on ${volume}, past the 1,500 threshold, at ${rate}.`;
  if (willScore(state)) return `${fmtInt(totals.events)} events ${when} on ${volume}, a ${rate} rate. The 1,500 threshold has not been passed yet.`;
  return `${fmtInt(totals.events)} events ${when} on ${volume}. Visa scores a CAID only in a month where it records 1,500 events.`;
}

export function step1Text(state: VampScopeState, unmatched: boolean): string {
  if (unmatched) return "No events can be matched to it.";
  switch (state) {
    case "past":
      return "Yes, it is past the 1,500 TC15/40 events mark.";
    case "likely":
      return "Likely - if the trend continues, on course to pass 1,500 events.";
    case "maybe":
      return "Possibly - the trend sits close to the 1,500 events threshold.";
    case "short":
      return "No, it stayed under the 1,500 events threshold.";
    default:
      return "No, it is under the 1,500 events threshold.";
  }
}

export function step2Text({ unmatched, rated, scored, rate }: VampReading): string {
  if (unmatched) return "–";
  if (!rated) return "No settled Visa transactions are recorded this month, so there is no rate to score.";
  if (!scored) return `Not scored this month. The rate is ${fmtPct(rate)}.`;
  return rate >= VAMP_EXCESSIVE_PCT ? `Yes - ${fmtPct(rate)}, over the line.` : "No - this is in Good Standing.";
}
